import { isXpayType, PaymentTypes } from 'commons/constants/paymentTypes'
import { FormStep } from 'commons/types/paymentTypes'

/**
 * Counts only alphabetic characters in a string.
 * Used to validate minimum character count while ignoring special characters.
 */
const countAlphabeticCharacters = (value: string): number => (value.match(/\p{L}/gu) || []).length

export type VerifyIdentityValidators = {
	validateFirstname: (firstname: string) => string | undefined
	validateLastname: (lastname: string) => string | undefined
	validateZipcode: (zipcode: string) => string | undefined
	validateCity: (city: string) => string | undefined
	validateCountry: (country: string) => string | undefined
	validateBirthdate: (birthdate: string, step: FormStep) => string | undefined
}

/**
 * Creates validation functions for the verify identity form.
 * Validation rules vary depending on the payment type (XPay has stricter requirements).
 */
export const createVerifyIdentityValidators = (
	paymentType: PaymentTypes,
	t: (key: string) => string
): VerifyIdentityValidators => ({
	/**
	 * Validation on first name:
	 * - At least 1 character is required
	 * - At least 2 alphabetic characters are required (only for XPay)
	 *   Special characters are not counted towards the minimum
	 */
	validateFirstname: (firstname: string): string | undefined => {
		if (!firstname.trim()) {
			return t('phone_form.verify_identity_firstname_error_required')
		}
		if (countAlphabeticCharacters(firstname) < 2 && isXpayType(paymentType)) {
			return t('phone_form.verify_identity_firstname_error_min_length')
		}
		return undefined
	},

	/**
	 * Validation on last name:
	 * - At least 1 character is required
	 * - At least 2 alphabetic characters are required (only for XPay)
	 *   Special characters are not counted towards the minimum
	 */
	validateLastname: (lastname: string): string | undefined => {
		if (!lastname.trim()) {
			return t('phone_form.verify_identity_lastname_error_required')
		}
		if (countAlphabeticCharacters(lastname) < 2 && isXpayType(paymentType)) {
			return t('phone_form.verify_identity_lastname_error_min_length')
		}
		return undefined
	},

	/**
	 * Validation on zipcode:
	 * - At least 1 character is required (only for XPay)
	 */
	validateZipcode: (zipcode: string): string | undefined => {
		if (!zipcode.trim() && isXpayType(paymentType)) {
			return t('phone_form.verify_identity_zipcode_error_required')
		}
		return undefined
	},

	/**
	 * Validation on city:
	 * - At least 1 character is required (only for XPay)
	 */
	validateCity: (city: string): string | undefined => {
		if (!city.trim() && isXpayType(paymentType)) {
			return t('phone_form.verify_identity_city_error_required')
		}
		return undefined
	},

	/**
	 * Validation on country:
	 * - At least 1 character is required (only for XPay)
	 */
	validateCountry: (country: string): string | undefined => {
		if (!country.trim() && isXpayType(paymentType)) {
			return t('phone_form.verify_identity_country_error_required')
		}
		return undefined
	},

	/**
	 * Validation on birthdate:
	 * - At least 1 character is required (only for XPay)
	 */
	validateBirthdate: (birthdate: string, step: FormStep): string | undefined => {
		if (step === FormStep.STEP_BIRTHDATE && !birthdate.trim()) {
			return t('phone_form.verify_identity_birthdate_error_required')
		}
		return undefined
	}
})
